import React from 'react';
import { AppBar, Toolbar, IconButton, Typography, Box, Menu, MenuItem, Tooltip, Button, Avatar, Badge } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdLogout } from "react-icons/md";
import { getData } from '../Webservice/webservice';

const Navbar = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  // State for handling dropdown menus dynamically
  const [menuAnchors, setMenuAnchors] = React.useState({});
  const timeoutRef = React.useRef({});

  const handleMenuOpen = (event, menuName) => {
    if (timeoutRef.current[menuName]) {
      clearTimeout(timeoutRef.current[menuName]);
    }
    setMenuAnchors(prev => ({ ...prev, [menuName]: event.currentTarget }));
  };

  const handleMenuClose = (menuName) => {
    timeoutRef.current[menuName] = setTimeout(() => {
      setMenuAnchors(prev => ({ ...prev, [menuName]: null }));
    }, 200); // Wait 200ms before closing
  };

  const handleMenuEnter = (menuName) => {
    if (timeoutRef.current[menuName]) {
      clearTimeout(timeoutRef.current[menuName]);
    }
  };

  const [menuItems, setMenuItems] = React.useState([]);

  const fetchUnreadCount = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await getData("vendor/notification/vend", { limit: 100 }, token);
      if (response && response.success && response.data) {
        const count = response.data.filter(notif => !notif.isRead).length;
        setUnreadCount(count);
      }
    } catch (error) {
      console.error("Failed to fetch unread count", error);
    }
  };

  React.useEffect(() => {
    fetchUnreadCount();
    // Refresh count every 20 seconds for near real-time updates
    const interval = setInterval(fetchUnreadCount, 20000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  React.useEffect(() => {
    const storedUser = sessionStorage.getItem('token'); // Check token existence
    // Mock user if needed or parse generic structure. The original code parsed 'user'.
    const storedUserJson = sessionStorage.getItem('user');
    
    if (storedUserJson) {
      try {
        const user = JSON.parse(storedUserJson);
        const dynamicMenus = [];

        // Full mapping for Parents and Children
        const menuMapping = {
          // Parent Menus
          'Dashboard': { text: "Dashboard", icon: <DashboardIcon sx={{ fontSize: 20, mr: 1 }} />, path: "/dashboard" },
          'Products': { text: "Products", icon: <InventoryIcon sx={{ fontSize: 20, mr: 1 }} /> }, // Parent
          'Operations': { text: "Operations", icon: <SettingsIcon sx={{ fontSize: 20, mr: 1 }} /> }, // Parent
          'Logs': { text: "Logs", icon: <HistoryIcon sx={{ fontSize: 20, mr: 1 }} /> }, // Parent

          // Submenus
          'Categories': { text: "Categories", icon: <InventoryIcon sx={{ fontSize: 18, mr: 1 }} />, path: "/categories" },
          'ProductManagement': { text: "Product Management", icon: <InventoryIcon sx={{ fontSize: 18, mr: 1 }} />, path: "/products" },
          'Coupons': { text: "Coupons", icon: <LocalOfferIcon sx={{ fontSize: 18, mr: 1 }} />, path: "/coupons" },
          'Online Orders': { text: "Online Orders", icon: <ShoppingCartIcon sx={{ fontSize: 18, mr: 1 }} />, path: "/orders" },
          'ActivityLog': { text: "Activity Log", icon: <HistoryIcon sx={{ fontSize: 18, mr: 1 }} />, path: "/activitylog" },
          'LoginLogs': { text: "Login Logs", icon: <FactCheckIcon sx={{ fontSize: 18, mr: 1 }} />, path: "/loginlogs" },
        };

        if (user.menus && Array.isArray(user.menus)) {
          user.menus.forEach(menu => {
            const mappedParent = menuMapping[menu.menuName];
            if (mappedParent) {
               const menuItem = { ...mappedParent, submenus: [] };
               if (menu.submenus && menu.submenus.length > 0) {
                  menu.submenus.forEach(sub => {
                      const mappedSub = menuMapping[sub.submenuName];
                      if (mappedSub) {
                         menuItem.submenus.push(mappedSub);
                      }
                  });
               }
               dynamicMenus.push(menuItem);
            }
          });
        }
        setMenuItems(dynamicMenus);
      } catch (error) {
        console.error("Failed to parse user menu data", error);
        setMenuItems([]);
      }
    }
  }, []);

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={Boolean(mobileMoreAnchorEl)}
      onClose={handleMobileMenuClose}
    >
      {menuItems.map((item) => (
        <React.Fragment key={item.text}>
           {item.submenus && item.submenus.length > 0 ? (
               <>
                  <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 'bold', fontFamily: 'Mulish, sans-serif' }}>
                      {item.icon} {item.text}
                  </MenuItem>
                  {item.submenus.map(sub => (
                      <MenuItem 
                        key={sub.text} 
                        onClick={() => { navigate(sub.path); handleMobileMenuClose(); }}
                        sx={{ pl: 4, fontFamily: 'Mulish, sans-serif' }}
                      >
                         <Box display="flex" alignItems="center">
                           {sub.icon} <Typography sx={{ml:1, fontFamily: 'Mulish, sans-serif', fontSize: '0.9rem'}}>{sub.text}</Typography>
                         </Box>
                      </MenuItem>
                  ))}
               </>
           ) : (
               <MenuItem 
                 onClick={() => { navigate(item.path); handleMobileMenuClose(); }}
                 selected={location.pathname === item.path}
                 sx={{ fontFamily: 'Mulish, sans-serif' }}
               >
                 <IconButton size="small" color="inherit">
                   {item.icon}
                 </IconButton>
                 <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.9rem' }}>{item.text}</Typography>
               </MenuItem>
           )}
        </React.Fragment>
      ))}
    </Menu>
  );

  return (
    <>
    <AppBar position="fixed" sx={{ height: '76px', backgroundColor: '#2B333A', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>  
      <Toolbar sx={{ minHeight: 'unset !important', px: { xs: 2, md: 4 } }}>
        {/* Mobile Menu Icon */}
        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 4 }} onClick={() => navigate('/')}>
            <img 
                src="./img/tvfmlogo.png" 
                alt="TVFM Vendor Logo"
                style={{ height: '42px', width: 'auto' }}
            />
        </Box>
        
        {/* Desktop Navigation Links */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            display: { xs: 'none', md: 'flex' },
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            alignItems: 'center',
            gap: 1.5,
            '&::-webkit-scrollbar': { height: '3px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '3px' },
          }}
        >
          {menuItems.map((page) => {
              const hasSubmenus = page.submenus && page.submenus.length > 0;
              const isActive = location.pathname === page.path || (hasSubmenus && page.submenus.some(sub => location.pathname === sub.path));

              return hasSubmenus ? (
                  <Box key={page.text} sx={{ position: 'relative' }}>
                       <Button
                          onMouseEnter={(e) => handleMenuOpen(e, page.text)}
                          onMouseLeave={() => handleMenuClose(page.text)}
                          sx={{ 
                            fontFamily:'Mulish, sans-serif',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            color: isActive ? '#fff' : '#cbd5e1', 
                            display: 'flex', 
                            alignItems: 'center',
                            textTransform: 'none',
                            px: 2,
                            py: 1,
                            borderRadius: '8px',
                            backgroundColor: Boolean(menuAnchors[page.text]) || isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.15)',
                              color: '#fff'
                            },
                          }}
                          startIcon={page.icon}
                          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                        >
                          {page.text}
                        </Button>
                        <Menu
                          anchorEl={menuAnchors[page.text]}
                          open={Boolean(menuAnchors[page.text])}
                          onClose={() => handleMenuClose(page.text)}
                          MenuListProps={{ 
                              onMouseEnter: () => handleMenuEnter(page.text),
                              onMouseLeave: () => handleMenuClose(page.text)
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          PaperProps={{ 
                              sx: { 
                                bgcolor: '#2B333A', 
                                color: 'white', 
                                minWidth: 220,
                                mt: 1,
                                borderRadius: '12px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                border: '1px solid rgba(255,255,255,0.1)'
                              },
                              onMouseEnter: () => handleMenuEnter(page.text),
                              onMouseLeave: () => handleMenuClose(page.text)
                          }}
                          disableRestoreFocus
                          sx={{ pointerEvents: 'none' }} 
                        >
                            {page.submenus.map((sub) => (
                                <MenuItem 
                                    key={sub.text} 
                                    onClick={() => {
                                        navigate(sub.path);
                                        setMenuAnchors(prev => ({ ...prev, [page.text]: null }));
                                    }}
                                    selected={location.pathname === sub.path}
                                    sx={{ 
                                        fontSize: '0.9rem',
                                        fontFamily:'Mulish, sans-serif',
                                        color: '#cbd5e1',
                                        py: 1.5,
                                        px: 2.5,
                                        pointerEvents: 'auto',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' },
                                        '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.2)', color: '#fff' },
                                        '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.3)' }
                                    }}
                                >
                                    {sub.icon} {sub.text}
                                </MenuItem>
                            ))}
                        </Menu>
                  </Box>
              ) : (
                <Button
                  key={page.text}
                  onClick={() => navigate(page.path)}
                  sx={{ 
                    fontFamily:'Mulish, sans-serif',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: isActive ? '#fff' : '#cbd5e1', 
                    display: 'flex', 
                    alignItems: 'center',
                    flexShrink: 0,
                    textTransform: 'none',
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    backgroundColor: isActive 
                      ? 'rgba(255, 255, 255, 0.15)' 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      color: '#fff'
                    },
                  }}
                  startIcon={page.icon}
                >
                  {page.text}
                </Button>
              );
          })}
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Notifications">
              <IconButton
                onClick={() => navigate('/notification')}
                size="small"
                sx={{  
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <Badge badgeContent={unreadCount} color="error" overlap="circular">
                  <NotificationsNoneIcon sx={{ fontSize: 32 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleMenu}
                size="small"
                sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white',
                    padding: '8px',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <AccountCircle sx={{ fontSize: 24 }} />
              </IconButton>
            </Tooltip>
        </Box>

        <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    mt: 1.5,
                    bgcolor: '#fff',
                    color: '#1e293b',
                    minWidth: 180,
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    '& .MuiMenuItem-root': {
                        fontSize: '0.95rem',
                        fontFamily: 'Mulish, sans-serif',
                        py: 1.5,
                        px: 2.5
                    }
                }
            }}
        >
            <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <AccountCircle sx={{ fontSize: 20, color: '#64748b' }} />
                    Profile
                </Box>
            </MenuItem>
            <MenuItem onClick={() => {
              handleClose();
              localStorage.removeItem('token');
              localStorage.removeItem('loginData');
              localStorage.removeItem('vendorToken');
              localStorage.removeItem('vendorUser');
              sessionStorage.clear();
              navigate('/login');
            }} sx={{ color: "#ef4444" }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <MdLogout size={20} />
                    Logout
                </Box>
            </MenuItem>
        </Menu>

      </Toolbar>
    </AppBar>
    {renderMobileMenu}
    </>
  );
};

export default Navbar;
